import { getAccessToken } from './auth';
import { API_URL } from './url';

// TODO: Fix typing below and come up with a complete model that incorporates the API calls too
type JsonDataObject = {
    [key: string]: string | number | boolean | null | JsonData | JsonData[];
};

interface JsonDataArray extends Array<string | number | boolean | null | JsonData | JsonData[]> {}

export type JsonData = JsonDataObject | JsonDataArray;

export function underscoreToCamelCase<T>(obj: JsonData): T {
    if (Array.isArray(obj)) {
        const newObj: JsonDataArray = obj.map((item) => underscoreToCamelCase(item as JsonData));
        return newObj as T;
    }
    if (typeof obj === 'object' && obj !== null) {
        const newObj: JsonData = {};
        for (const key in obj) {
            if (Object.hasOwn(obj, key)) {
                const camelCaseKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
                newObj[camelCaseKey] = underscoreToCamelCase(obj[key] as JsonData);
            }
        }
        return newObj as T;
    }
    return obj as T;
}

export function camelCaseToUnderscore<T>(obj: T): JsonData {
    if (Array.isArray(obj)) {
        const newObj: JsonDataArray = obj.map((item) => underscoreToCamelCase(item));
        return newObj;
    }
    if (typeof obj === 'object' && obj !== null) {
        const newObj: JsonData = {};
        for (const key in obj) {
            if (Object.hasOwn(obj, key)) {
                const underscoreKey = key.replace(/([A-Z])/g, (g) => `_${g.toLowerCase()}`);
                newObj[underscoreKey] = camelCaseToUnderscore(obj[key]);
            }
        }
        return newObj;
    }
    return obj as JsonData;
}

export const http = {
    async post<T>(path: string, body: Record<string, unknown> | Record<string, unknown>[]): Promise<T> {
        const token = await getAccessToken();
        const url = `${API_URL}${path}`;

        const response = await fetch(url, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json, text/plain',
                'Content-Type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(body),
        });

        if (response.ok) {
            try {
                return await response.json();
            } catch (e) {
                return {} as T;
            }
        }
        throw new Error('Failed to fetch data');
    },

    async get<T>(path: string, query?: Record<string, unknown>): Promise<T> {
        const token = await getAccessToken();
        const queryString = query ? `?${new URLSearchParams(query as Record<string, string>).toString()}` : '';
        const url = `${API_URL}${path}${queryString}`;

        const response = await fetch(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (response.ok) {
            return response.json().then((data) => underscoreToCamelCase(data));
        }
        throw new Error('Failed to fetch data');
    },

    async getFile(absPath: string, mimeType: string): Promise<Response> {
        const token = await getAccessToken();
        const response = await fetch(absPath, {
            headers: {
                Accept: mimeType,
            },
        });

        if (response.ok) {
            return response;
        }
        throw new Error('Failed to fetch data');
    },

    async delete<T>(path: string): Promise<T> {
        const token = await getAccessToken();
        const url = `${API_URL}${path}`;

        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        if (response.ok) {
            try {
                return await response.json().then((data) => underscoreToCamelCase(data));
            } catch (e) {
                return {} as T;
            }
        }
        throw new Error('Failed to fetch data');
    },

    async put<T>(path: string, body: Record<string, unknown> | Record<string, unknown>[]): Promise<T> {
        const token = await getAccessToken();
        const url = `${API_URL}${path}`;

        const response = await fetch(url, {
            method: 'PUT',
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json, text/plain',
                'Content-Type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(body),
        });

        if (response.ok) {
            return response.json().then((data) => underscoreToCamelCase(data));
        }
        throw new Error('Failed to fetch data');
    },

    async patch<T>(path: string, body: Record<string, unknown> | Record<string, unknown>[]): Promise<T> {
        const token = await getAccessToken();
        const url = `${API_URL}${path}`;

        const response = await fetch(url, {
            method: 'PATCH',
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json, text/plain',
                'Content-Type': 'application/json;charset=UTF-8',
            },
            body: JSON.stringify(body),
        });

        if (response.ok) {
            try {
                return await response.json().then((data) => underscoreToCamelCase(data));
            } catch (e) {
                return {} as T;
            }
        }
        throw new Error('Failed to fetch data');
    },

    async upload(
        path: string,
        method: string,
        body: FormData,
        onProgress: (ev: ProgressEvent) => void,
        onDone: (ev: ProgressEvent) => void,
    ): Promise<XMLHttpRequest> {
        const token = await getAccessToken();
        const url = `${API_URL}${path}`;

        const xhr = new XMLHttpRequest();
        xhr.open(method, url, true);
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.upload.onprogress = onProgress;
        xhr.onload = onDone;
        xhr.send(body);
        return xhr;
    },
};
